import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAbility } from './AbilityProvider';
import { useAuth } from './AuthProvider';

import { RouteFallback } from '@/components/RouteFallback';
import { canAll, type AbilityCheck } from '@/lib/casl';

// Lazy so the 403 panel and the suspended screen stay out of the initial
// bundle (§1.5).
const Forbidden = lazy(() => import('./Forbidden'));
const TenantSuspendedPage = lazy(() => import('./TenantSuspendedPage'));

interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * Abilities the route needs (ADR-0005 §7.4, ADR-0009). Every check must
   * pass. UX only: the API checks every request. Without it the route never
   * waits on the ability rules.
   */
  requires?: AbilityCheck | readonly AbilityCheck[];
}

export function ProtectedRoute({ children, requires }: ProtectedRouteProps): ReactNode {
  const { status, signedOutReason } = useAuth();
  const { ability, status: abilityStatus } = useAbility();
  const location = useLocation();

  // The clinic was suspended mid-session: say so plainly instead of a
  // generic error or a silent bounce to sign in.
  if (signedOutReason === 'tenant_suspended') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <TenantSuspendedPage />
      </Suspense>
    );
  }

  if (status === 'idle' || status === 'refreshing') {
    return <RouteFallback />;
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (requires === undefined) {
    return <>{children}</>;
  }

  if (abilityStatus === 'loading') {
    return <RouteFallback />;
  }

  if (abilityStatus === 'error') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Forbidden retry />
      </Suspense>
    );
  }

  const checks: readonly AbilityCheck[] = isCheckList(requires) ? requires : [requires];
  // A denial shows the 403 panel in place. It never bounces to /login: the
  // user is signed in, just not allowed here.
  if (!canAll(ability, checks)) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Forbidden />
      </Suspense>
    );
  }

  return <>{children}</>;
}

function isCheckList(
  requires: AbilityCheck | readonly AbilityCheck[],
): requires is readonly AbilityCheck[] {
  return Array.isArray(requires);
}

export default ProtectedRoute;
